/**
 * Affichage immédiat des messages et réactions d'un spectateur. Par live-api, un message n'existe
 * qu'après le consumer, et son écho Ably arrive quelques secondes après l'envoi : on l'affiche dès
 * l'envoi puis on le rattache à son écho par le `commandId`, qui seul identifie la commande sans
 * ambiguïté (deux messages identiques d'un même spectateur restent distincts).
 */

/** Message envoyé dont l'écho n'est pas encore arrivé. Confirmé, il n'a plus d'état. */
export type ChatDeliveryState = 'pending' | 'uncertain' | 'failed';

/** Au-delà, un message accepté sans écho est signalé comme non confirmé, sans être tenu pour perdu. */
export const CHAT_CONFIRMATION_TIMEOUT_MS = 10_000;

/** Durée pendant laquelle l'écho d'une réaction envoyée est attendu pour ne pas la compter deux fois. */
export const PENDING_REACTION_TTL_MS = 10_000;

type ChatListMessage = { id?: string; data?: unknown; timestamp?: number };

export type PendingChatAuthor = {
  userId: number | string;
  userName: string;
  subscriptionId?: number;
  statusLabel?: string;
  statusColor?: string;
  statusIcon?: string;
  banned?: boolean;
};

const PUBLIC_PARTICIPANT_NAME = 'Participant';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function readRecord(data: unknown): Record<string, unknown> | null {
  return data != null && typeof data === 'object' && !Array.isArray(data)
    ? (data as Record<string, unknown>)
    : null;
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** `commandId` du message affiché à l'envoi. */
export function readClientCommandId(data: unknown): string | null {
  return readString(readRecord(data)?.clientCommandId) ?? null;
}

/** `commandId` porté par l'écho Ably d'un message passé par live-api. */
export function readEchoCommandId(data: unknown): string | null {
  return readString(readRecord(data)?.commandId) ?? null;
}

export function readDeliveryState(data: unknown): ChatDeliveryState | null {
  const state = readRecord(data)?.deliveryState;
  return state === 'pending' || state === 'uncertain' || state === 'failed' ? state : null;
}

/** Même règle que le back : prénom vide ou adresse e-mail, le chat affiche « Participant ». */
export function toPublicAuthorName(firstName: string | null | undefined): string {
  const name = firstName?.trim() ?? '';
  return name === '' || EMAIL_PATTERN.test(name) ? PUBLIC_PARTICIPANT_NAME : name;
}

/**
 * Auteur du message affiché à l'envoi, repris du dernier message confirmé du spectateur pour que
 * nom et statut ne bougent pas à l'arrivée de l'écho. Sans message précédent, le prénom connu.
 */
export function resolvePendingChatAuthor(
  messages: readonly ChatListMessage[],
  currentUserId: string,
  fallbackName: string | null | undefined,
): PendingChatAuthor {
  for (let index = messages.length - 1; index >= 0; index--) {
    const data = readRecord(messages[index].data);
    if (!data || data.id == null || data.teamMember === true) continue;
    if (data.userId == null || String(data.userId) !== currentUserId) continue;
    return {
      userId: data.userId as number | string,
      userName: readString(data.userName) ?? toPublicAuthorName(fallbackName),
      subscriptionId: typeof data.subscriptionId === 'number' ? data.subscriptionId : undefined,
      statusLabel: readString(data.statusLabel),
      statusColor: readString(data.statusColor),
      statusIcon: readString(data.statusIcon),
      banned: data.banned === true,
    };
  }
  const numericUserId = Number(currentUserId);
  return {
    userId: currentUserId !== '' && Number.isFinite(numericUserId) ? numericUserId : currentUserId,
    userName: toPublicAuthorName(fallbackName),
  };
}

/**
 * Message affiché dès l'envoi. Jamais avant le dernier message de la liste : une horloge locale en
 * retard le placerait plus haut que le bas du fil.
 */
export function buildPendingChatMessage({
  commandId,
  content,
  author,
  now,
  latestTimestamp,
}: {
  commandId: string;
  content: string;
  author: PendingChatAuthor;
  now: number;
  latestTimestamp: number;
}): ChatListMessage {
  return {
    id: `pending:${commandId}`,
    timestamp: Math.max(now, latestTimestamp + 1),
    data: {
      ...author,
      text: content,
      content,
      teamMember: false,
      clientCommandId: commandId,
      deliveryState: 'pending' satisfies ChatDeliveryState,
    },
  };
}

/**
 * Rattache l'écho Ably au message affiché à l'envoi : il en prend le contenu et l'id définitif,
 * mais garde sa clé et son horodatage, donc sa place dans le fil, sans démontage. Liste inchangée
 * si l'écho ne correspond à aucun message en attente.
 */
export function adoptConfirmedChatMessage<T extends ChatListMessage>(
  messages: readonly T[],
  incoming: T,
): readonly T[] {
  const commandId = readEchoCommandId(incoming.data);
  if (!commandId) return messages;
  const index = messages.findIndex((msg) => readClientCommandId(msg.data) === commandId);
  if (index < 0) return messages;
  const local = messages[index];
  if (readDeliveryState(local.data) == null) return messages;
  const confirmed = {
    ...incoming,
    id: local.id,
    timestamp: local.timestamp,
    data: { ...readRecord(incoming.data), clientCommandId: commandId },
  };
  return messages.map((msg, position) => (position === index ? confirmed : msg));
}

function canMoveTo(from: ChatDeliveryState, to: ChatDeliveryState): boolean {
  if (to === 'uncertain') return from === 'pending';
  if (to === 'failed') return from !== 'failed';
  return from !== 'pending';
}

/** Change l'état d'un message encore en attente. Un message confirmé ne revient jamais en arrière. */
export function setChatDeliveryState<T extends ChatListMessage>(
  messages: readonly T[],
  commandId: string,
  state: ChatDeliveryState,
): readonly T[] {
  const index = messages.findIndex((msg) => readClientCommandId(msg.data) === commandId);
  if (index < 0) return messages;
  const local = messages[index];
  const current = readDeliveryState(local.data);
  if (current == null || !canMoveTo(current, state)) return messages;
  const updated = { ...local, data: { ...readRecord(local.data), deliveryState: state } };
  return messages.map((msg, position) => (position === index ? updated : msg));
}

/**
 * Un refus du back (4xx) prouve que le message n'est pas parti. Un timeout, une coupure réseau ou
 * une 5xx ne prouvent rien : la commande a pu être acceptée, l'écho peut encore arriver.
 */
export function classifyChatSendFailure(error: unknown): 'failed' | 'uncertain' {
  const status = readRecord(error)?.status;
  return typeof status === 'number' && status < 500 && status !== 408 ? 'failed' : 'uncertain';
}

export type PendingChatReaction = { messageId: string; emoji: string; expiresAt: number };

function sameEmoji(left: string, right: string): boolean {
  return left.trim() === right.trim();
}

/**
 * La réaction envoyée est déjà comptée : le premier écho identique est absorbé au lieu d'être
 * compté une seconde fois. Peu importe que cet écho soit le sien ou celui d'un autre spectateur,
 * le total affiché reste juste. Les attentes expirées sont purgées au passage.
 */
export function consumePendingReaction(
  pending: readonly PendingChatReaction[],
  messageId: string,
  emoji: string,
  now: number,
): { remaining: PendingChatReaction[]; consumed: boolean } {
  const alive = pending.filter((reaction) => reaction.expiresAt > now);
  const index = alive.findIndex(
    (reaction) => reaction.messageId === messageId && sameEmoji(reaction.emoji, emoji),
  );
  if (index < 0) return { remaining: alive, consumed: false };
  return { remaining: alive.filter((_, position) => position !== index), consumed: true };
}
